import React, { useState, useRef, useEffect } from 'react';
import {
  Fab,
  Dialog,
  DialogContent,
  TextField,
  IconButton,
  List,
  ListItem,
  Typography,
  Box,
  Paper,
  Avatar,
  Chip,
  Stack,
  useTheme,
} from '@mui/material';

import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';

/* ================= BRAND COLORS ================= */
const BRAND_COLORS = {
  primary: '#0c0f4c',        // Website primary (blue)
  secondary: '#443bcf',      // CTA red

  bgMain: '#FFFFFF',
  bgSoft: '#F5F6FA',
  chipBg: '#E6E8F0',

  text: '#000000',
  textMuted: '#4B5563',
  textInverse: '#FFFFFF',
};

/* ================= SUGGESTIONS ================= */
const SUGGESTED_QUERIES = [
  // 'How PitchHere works for Startup?',
  // 'What is this platform about?',
  // 'How to contact Investor on PitchHere?',
  // 'Tell me about the Investor matching process.',
  'What is PitchHere?',
  'Who can use the platform?',
  'Is there a cost for startups to join?',
  'What is PitchHere’s mission?',
];



/* ================= MESSAGE RENDER ================= */
const renderMessageContent = (text) => (
  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
    {text}
  </Typography>
);

/* ================= CHAT BUBBLE ================= */
const ChatBubble = ({ msg, children }) => {
  const isUser = msg.from === 'user';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        maxWidth: '80%',
        borderRadius: isUser
          ? '18px 18px 4px 18px'
          : '18px 18px 18px 4px',
        backgroundColor: isUser
          ? BRAND_COLORS.secondary
          : BRAND_COLORS.chipBg,
        color: isUser
          ? BRAND_COLORS.textInverse
          : BRAND_COLORS.text,
        border: !isUser
          ? `1px solid ${BRAND_COLORS.primary}`
          : 'none',
      }}
    >
      {children}
    </Paper>
  );
};

/* ================= WELCOME ================= */
const WelcomeSuggestions = ({ onClick }) => (
  <Box sx={{ p: 2 }}>
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      Try asking:
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
      {SUGGESTED_QUERIES.map((q) => (
        <Chip
          key={q}
          label={q}
          onClick={() => onClick(q)}
          sx={{
            cursor: 'pointer',
            bgcolor: BRAND_COLORS.bgMain,
            border: `1px solid ${BRAND_COLORS.secondary}`,
            color: BRAND_COLORS.secondary,
          }}
        />
      ))}
    </Stack>
  </Box>
);

/* ================= MAIN COMPONENT ================= */
const FloatingChatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: 'bot',
      text: 'Hi! I am PitchHere Support. How can I help you today?',
      timestamp: new Date().toLocaleTimeString(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    window.parent.postMessage(
      { type: open ? 'CHAT_OPEN' : 'CHAT_CLOSE' },
      '*'
    );
  }, [open]);
  const sendToBackend = async (text) => {
    setIsLoading(true);
    try {
      const res = await fetch(
        'https://phitchhere-chatbot-backend.onrender.com/api/chat',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: text }),
        }
      );
      const data = await res.json();

      setMessages((prev) => [
        ...prev,
        {
          from: 'bot',
          text: data.response,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          from: 'bot',
          text: 'Server is not reachable. Please try again.',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text) return;

    setMessages((prev) => [
      ...prev,
      {
        from: 'user',
        text,
        timestamp: new Date().toLocaleTimeString(),
      },
    ]);

    setInput('');
    await sendToBackend(text);
  };

  return (
    <>
      {/* FAB */}
      {!open && (
        <Fab
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            width: 72,
            height: 72,
            bgcolor: BRAND_COLORS.primary,
            color: BRAND_COLORS.textInverse,
          }}
        >
          <ChatIcon />
        </Fab>
      )}

      {/* CHAT WINDOW */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        hideBackdrop
        PaperProps={{
          elevation: 0,
          sx: {
            position: 'fixed',
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >



        {/* HEADER */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: BRAND_COLORS.primary,
            color: BRAND_COLORS.textInverse,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box display="flex" gap={1} alignItems="center">
          <Box
              component="img"
              src="/ph-icon.png"
              alt="PitchHere"
              sx={{
                width: 36,
                height: 36,
                objectFit: 'contain',
                borderRadius: '50%',
                border: `1px solid ${BRAND_COLORS.secondary}`,
                backgroundColor: BRAND_COLORS.bgMain,
                p: 0.5,
              }}
            />
            <Typography fontWeight={600}>
              PitchHere Support
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ color: BRAND_COLORS.textInverse }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* BODY */}
        <DialogContent sx={{ bgcolor: BRAND_COLORS.bgSoft }}>
          <List>
            {messages.map((msg, i) => (
              <ListItem
                key={i}
                sx={{
                  justifyContent:
                    msg.from === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <ChatBubble msg={msg}>
                  {renderMessageContent(msg.text)}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      color:
                        msg.from === 'user'
                          ? 'rgba(255,255,255,0.75)'
                          : BRAND_COLORS.textMuted,
                    }}
                  >
                    {msg.timestamp}
                  </Typography>
                </ChatBubble>
              </ListItem>
            ))}
            <div ref={messagesEndRef} />
          </List>

          {messages.length === 1 && (
            <WelcomeSuggestions onClick={sendMessage} />
          )}
        </DialogContent>

        {/* INPUT */}
        <Box
          sx={{
            p: 1.5,
            borderTop: '1px solid #ddd',
            display: 'flex',
            gap: 1,
            bgcolor: BRAND_COLORS.bgMain,
          }}
        >
          <TextField
            fullWidth
            size="small"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            sx={{
              '& input': { color: BRAND_COLORS.text },
              '& input::placeholder': {
                color: BRAND_COLORS.textMuted,
                opacity: 1,
              },
            }}
          />
          <IconButton
            onClick={() => sendMessage()}
            sx={{
              bgcolor: BRAND_COLORS.primary,
              color: BRAND_COLORS.textInverse,
              '&:hover': { bgcolor: BRAND_COLORS.primary },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Dialog>
    </>
  );
};

export default FloatingChatbot;

